// Copyright (c) Docugami, Inc. All rights reserved.

import { Page } from "sdv/Page";
import { PageDataDownloader, PageDataWindow } from "sdv/PageDataDownloader";
import { scaleValue, transformTokenBoxesPageToWorldSpace, WorldSpaceConfiguration } from "sdv/Space";
import { ISystem } from "../engine/Engine";
import { IResources, PageData } from "../Resources";
import { dgmlSlice } from "store/dgml/dgmlSlice";
import { folderIdSelector } from "store/dgml/dgmlSelectors";
import { store } from "store";
import { pageImageSelector } from "store/frontend/frontendSlice";
import { createSelector } from "@reduxjs/toolkit";

const forwardWindow: PageDataWindow = [0, 2];
const backwardWindow: PageDataWindow = [2, 0];

/**
 * System that is responsible for loading page data. Today, this includes requesting page images
 * and fetching page tokens as those pages become visible.
 */
export class LoadPagesSystem implements ISystem {
    private readonly _imageDownloader: PageDataDownloader;
    private readonly _tokensDownloader: PageDataDownloader;
    private readonly _config: WorldSpaceConfiguration;

    private _lastViewPos: number = 0;

    get updateEnabled() { return true; }

    resources!: IResources;

    constructor(imageDownloader: PageDataDownloader, tokensDownloader: PageDataDownloader, config: WorldSpaceConfiguration) {
        this._imageDownloader = imageDownloader;
        this._tokensDownloader = tokensDownloader;
        this._config = config;
    }

    update() {
        this.handleTextVersionChanges();
        this.downloadPageData();
        this.loadPageImages();
        this.createPages();
        this.loadPageTokenLocations();
        this.updatePagesLoaded();
    }

    private handleTextVersionChanges() {
        if (this.resources.check("textVersion")) {
            this.resources.set("pageTokens", {});
            this.resources.set("pageLoadState", {});
            this.resources.set("pagesLoaded", false);
        }
    }

    // Loads data for the visible pages
    private downloadPageData() {
        const viewPos = this.resources!.get("viewPosition").value.y;
        const isForward = viewPos - this._lastViewPos >= 0;
        this._lastViewPos = viewPos;

        if (!this.resources.check("visiblePages") && !this.resources.check("textVersion")) {
            return;
        }

        const visiblePages = this.resources.get("visiblePages").value;
        for (const visiblePage of visiblePages) {
            this._imageDownloader.add(visiblePage.number, isForward ? forwardWindow : backwardWindow);
            this._tokensDownloader.add(visiblePage.number, isForward ? forwardWindow : backwardWindow);
        }
    }

    private updatePagesLoaded() {
        if (this.resources.get("pagesLoaded").value && !this.resources.check("visiblePages") && !this.resources.check("pageLoadState")) {
            return;
        }

        const visiblePages = this.resources.get("visiblePages").value;
        const pageLoadState = this.resources.get("pageLoadState").value;
        const pageImages = this.resources.get("pageImages").value;

        let isLoaded = visiblePages.length > 0 && !visiblePages.some(x => !pageImages[x.id]?.complete || ((pageLoadState[x.id] ?? 0) & PageData.All) !== PageData.All)
        this.resources.set("pagesLoaded", isLoaded);
    }

    // Transforms the raw page state from the network into our renderable SDV pages.  Dynamically
    // updates the pages if the underlying data or view scaling changes
    private createPages() {
        if (this.resources.check("rawPages") || this.resources.check("worldSpace")) {
            const worldWidth = this.resources.get("worldSpace").value!.width;
            const rawPages = this.resources.get("rawPages").value;
            if (worldWidth > 0 && rawPages.length > 0) {
                let posY = 0;
                const newPages: Page[] = [];
                for (const rawPage of rawPages) {
                    const pageToWorldScaleFactor = worldWidth / rawPage.width;
                    const pageHeight = scaleValue(rawPage.height, pageToWorldScaleFactor);
                    const pageWidth = scaleValue(rawPage.width, pageToWorldScaleFactor);

                    const newPage: Page = {
                        id: rawPage.number.toString(),
                        number: rawPage.number,
                        pageIndex: rawPage.number - 1,
                        width: pageWidth,
                        height: pageHeight,
                        sourceWidth: rawPage.width,
                        sourceHeight: rawPage.height,
                        position: { x: 0, y: posY },
                        tokenRanges: rawPage.tokenRanges,
                        scaleFactor: pageToWorldScaleFactor
                    };

                    posY += pageHeight + this._config.pageBreakHeight;
                    newPages.push(newPage);
                }

                this.resources.set("pages", newPages);
            }
        }
    }

    private loadPageTokenLocations() {
        if (!this.resources.get("pagesLoaded").value) {
            const textVersion = this.resources.get("textVersion").value!;
            if (!!textVersion) {
                const pageLoadState = this.resources.get("pageLoadState").value;
                const pages = this.resources.get("visiblePages").value;
                const folderId = folderIdSelector(this.resources.get("documentId").value!)!;
                let pageTokens = this.resources.get("pageTokens").value;
                let loadStateUpdated = false;
                for (const page of pages) {
                    let loadState = pageLoadState[page.id] ?? 0;
                    if (loadState & PageData.TokenLocations) {
                        continue;
                    }

                    if (this._tokensDownloader.isLoaded(page.number)) {
                        const tokenLocations = dgmlSlice.endpoints.getTokenLocations.select({
                            docsetId: folderId.clusterId,
                            fileId: folderId.fileId,
                            page: page.number,
                            version: textVersion
                        })(store.getState()).data?.value;

                        loadState |= PageData.TokenLocations;
                        const newTokens = transformTokenBoxesPageToWorldSpace(page, tokenLocations ?? {});
                        pageLoadState[page.id] = loadState;
                        pageTokens = { ...pageTokens, ...newTokens };
                        loadStateUpdated = true;
                    }
                }

                this.resources.set("pageTokens", pageTokens);

                if (loadStateUpdated) {
                    this.resources.set("pageLoadState", { ...pageLoadState })
                }
            }
        }
    }

    private loadPageImages() {
        if (!this.resources.get("pagesLoaded").value) {
            const pageLoadState = this.resources.get("pageLoadState").value;
            const pages = this.resources.get("visiblePages").value;
            const documentId = this.resources.get("documentId").value;
            for (const page of pages) {
                let loadState = pageLoadState[page.id] ?? 0;
                if (loadState & PageData.Image) {
                    continue;
                }
                else if (this._imageDownloader.isLoaded(page.number)) {
                    const image = pageHtmlImageSelector(documentId, page.number);
                    loadState |= PageData.Image;
                    this.resources.setFn("pageImages", images => ({...images, [page.id]: image}));
                    this.resources.setFn("pageLoadState", state => ({ ...state, [page.id]: loadState }))
                }
            }
        }
    }
}

const pageHtmlImageSelector = createSelector(
    [
        (documentId: string, page: number) => folderIdSelector(documentId)!,
        (fileId: string, page: number) => page
    ],
    (folderId, page) => {
        const imageUrl = pageImageSelector(folderId.fileId, page)(store.getState()).data;
        if (!imageUrl) {
            throw new Error('Image not yet downloaded.');
        }

        const image = new Image();
        image.src = imageUrl;
        return image;
    },
    {
        memoizeOptions: {
            maxSize: 250
        }
    }
)
